import type { Ref } from 'vue';
import { nextTick, ref, watch } from 'vue';
import { isDeepEqual } from '#layers/base';

export function useInitializedDisplayOptionsModel<T>(
	sources: {
		baselineDisplayOptions?: () => T | undefined;
		displayOptions: () => T | undefined;
		initializedBaselineOptions?: () => T | undefined;
		initializedOptions: () => T;
	},
	emit: (value: T | undefined) => void,
): Ref<T | undefined> {
	const model = ref<T>() as Ref<T | undefined>;
	let suppressEmit = false;

	function syncModelFromProps() {
		const nextValue = sources.initializedOptions();
		if (isDeepEqual(nextValue, model.value)) return;

		suppressEmit = true;
		model.value = nextValue;

		nextTick(() => {
			suppressEmit = false;
		});
	}

	watch([
		sources.initializedOptions,
		sources.displayOptions,
		...(sources.initializedBaselineOptions ? [sources.initializedBaselineOptions] : []),
		...(sources.baselineDisplayOptions ? [sources.baselineDisplayOptions] : []),
	], syncModelFromProps, { immediate: true });

	watch(model, (value) => {
		if (suppressEmit) return;

		const displayOptions = sources.displayOptions();
		if (!value || isDeepEqual(value, displayOptions)) return;

		const initializedBaselineOptions = sources.initializedBaselineOptions?.();

		if (initializedBaselineOptions && isDeepEqual(value, initializedBaselineOptions)) {
			emit(sources.baselineDisplayOptions?.());
			return;
		}

		if (isDeepEqual(value, sources.initializedOptions())) {
			emit(displayOptions);
			return;
		}

		emit(value);
	}, { deep: true });

	return model;
}
